import React, { useEffect, useRef } from "react";
import type { CSSProperties } from "react";
import { useSlide3 } from "./slide3.data";
import { FONT_BODY, FONT_DISPLAY, FONT_MONO } from "theme/fonts";

/* Portrait/mobile layout for Slide 3 — flowing, scrollable, no canvas.
   Reads the same content as the desktop slide from slide3.data.ts. */

const accent = "#4fc3f7";
const font = FONT_BODY;
const mono = FONT_MONO;

const attrStyle: CSSProperties = { fontFamily: mono, fontSize: 10, letterSpacing: 1.5, color: accent, textTransform: "uppercase", margin: "11px 0 3px" };
const valStyle: CSSProperties = { fontFamily: FONT_BODY, fontStyle: "normal", fontSize: 15, lineHeight: 1.5, color: "#c2d8ec", margin: 0 };

const HTLDSlide3Mobile: React.FC = () => {
  const { layers: slide3Layers, meta: slide3Meta } = useSlide3();
  const lb = slide3Meta.labels;
  const fontRef = useRef<HTMLLinkElement | null>(null);
  useEffect(() => {
    if (!document.querySelector('link[href*="Space+Grotesk"]')) {
      const l = document.createElement("link"); l.rel = "stylesheet";
      l.href = "https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;1,300;1,400;1,700&display=swap";
      document.head.appendChild(l); fontRef.current = l;
    }
  }, []);

  return (
    <div style={{ background: "#040f18", color: "#d0e8f8", fontFamily: font, padding: "22px 18px 40px", boxSizing: "border-box", height: "100%", width: "100%", overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
      <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: 2, color: "#5888b0", textTransform: "uppercase", marginBottom: 6 }}>
        {slide3Meta.breadcrumbPre} · <span style={{ color: accent }}>{slide3Meta.breadcrumbAccent}</span>
      </div>
      <h1 style={{ fontFamily: FONT_DISPLAY, fontStyle: "normal", fontWeight: 600, fontSize: 26, lineHeight: 1.15, margin: "0 0 4px", color: "#e8f4ff" }}>
        {slide3Meta.titlePlain} <span style={{ color: accent }}>{slide3Meta.titleAccent}</span>
      </h1>
      <p style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontWeight: 300, fontSize: 14, color: "#7aa8cc", margin: "0 0 18px" }}>
        {slide3Meta.journeySurface} · {slide3Meta.journeyWaking} &nbsp;&rarr;&nbsp; {slide3Meta.journeyDeep} · {slide3Meta.journeyBelow}
      </p>

      {slide3Layers.map((L) => (
        <div key={L.key} style={{
          border: `1px solid ${L.highlight ? "rgba(79,195,247,0.4)" : "rgba(79,195,247,0.16)"}`,
          background: L.highlight ? "rgba(79,195,247,0.08)" : "rgba(8,24,40,0.55)",
          borderRadius: 12, padding: "14px 16px 16px", marginBottom: 14,
        }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
            <div style={{ fontFamily: FONT_DISPLAY, fontStyle: "normal", fontWeight: 600, fontSize: 18, color: L.color }}>{L.name}</div>
            <div style={{ fontFamily: mono, fontSize: 9, letterSpacing: 1.5, color: "#5a7890" }}>{L.tag}</div>
          </div>

          <div style={attrStyle}>{lb.awareness}</div><p style={valStyle}>{L.awareness}</p>
          <div style={attrStyle}>{lb.role}</div><p style={valStyle}>{L.role}</p>
          <div style={attrStyle}>{lb.accessibility}</div><p style={valStyle}>{L.accessibility}</p>
          <div style={attrStyle}>{lb.sleep}</div><p style={valStyle}>{L.sleep}</p>
          <div style={attrStyle}>{lb.waves}</div>
          <p style={{ ...valStyle, color: L.highlight ? "#cbe4fb" : "#a8c8e8" }}>
            {L.waves.map((w) => `${w.symbol} ${w.name}${slide3Meta.waveSep}${w.desc}`).join("  ·  ")}
            {L.wavesNote ? `  ·  ${L.wavesNote}` : ""}
          </p>
        </div>
      ))}

      <div style={{ borderTop: "1px solid rgba(79,195,247,0.12)", paddingTop: 12, marginTop: 4 }}>
        <span style={{ fontFamily: FONT_BODY, color: accent, fontStyle: "normal", fontSize: 14 }}>{slide3Meta.footnoteLead} </span>
        <span style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontSize: 14, lineHeight: 1.55, color: "#8fb2cc" }}>{slide3Meta.footnoteBody}</span>
      </div>
    </div>
  );
};

export default HTLDSlide3Mobile;
